/* Shared product-card behaviour for the cream/glass cards used on the shop
 * grid (art-collection) and recommendation surfaces (art-recommendations).
 * Two pieces, both delegated on `document` so they survive AJAX swaps and work
 * regardless of which section rendered the cards:
 *   1) quick add-to-cart on the card + the dialog form
 *   2) the "Choose options" dialog (portaled to <body>)
 * Guarded so including the asset from multiple sections binds only once.
 */
(function () {
  if (window.__artProductCardInit) return;
  window.__artProductCardInit = true;

  /* ---- 1) Quick add-to-cart -------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-atc-form]');
    if (!form) return;
    e.preventDefault();

    var btn = form.querySelector('[data-atc]');
    if (btn && btn.classList.contains('is-loading')) return;

    var addUrl = form.getAttribute('action') + '.js'; // /cart/add → /cart/add.js
    var body = new FormData(form);

    if (btn) { btn.classList.add('is-loading'); btn.classList.remove('is-added'); }

    // fetch() to /cart/add.js is intercepted by the header script, which
    // refreshes the cart badge and fires the sparkle burst.
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: body
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!btn) return;
        btn.classList.remove('is-loading');
        if (res.ok) {
          btn.classList.add('is-added');
          clearTimeout(btn._atcTimer);
          btn._atcTimer = setTimeout(function () { btn.classList.remove('is-added'); }, 1900);
          document.dispatchEvent(new CustomEvent('art:cart:added', { detail: { form: form } }));
        }
      })
      .catch(function () {
        if (btn) btn.classList.remove('is-loading');
      });
  });

  /* ---- 2) "Choose options" dialog -------------------------------------- */
  function initChoose() {
    var modal = document.querySelector('[data-choose-modal]');
    if (!modal || modal._artInit) return;
    modal._artInit = true;

    // Portal the dialog to <body> so `position: fixed` covers the whole
    // viewport — inside a section a transformed/containing-block ancestor
    // clips it (overlay only blurs part of the page, panel floats high).
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var panel = modal.querySelector('.art-choose__panel');
    var imgEl = modal.querySelector('[data-choose-img]');
    var mediaEl = modal.querySelector('.art-choose__media');
    var titleEl = modal.querySelector('[data-choose-title]');
    var priceEl = modal.querySelector('[data-choose-price]');
    var optionsEl = modal.querySelector('[data-choose-options]');
    var idInput = modal.querySelector('[data-choose-id]');
    var addBtn = modal.querySelector('[data-choose-add]');
    var addLabel = modal.querySelector('.art-choose__add-default');
    var viewLink = modal.querySelector('[data-choose-view]');

    var current = null;   // parsed product data
    var selected = [];    // chosen option value per option index
    var lastFocus = null;
    var closeTimer = null;

    function getProductData(id) {
      var el = document.querySelector('script[data-product-json="' + id + '"]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    // Show/hide the media pane based on whether the product has an image.
    function setMedia(src) {
      if (src) {
        imgEl.src = src;
        if (mediaEl) mediaEl.hidden = false;
      } else {
        imgEl.removeAttribute('src');
        if (mediaEl) mediaEl.hidden = true;
      }
    }

    // Unique values for option index i, in first-seen order.
    function valuesFor(i) {
      var seen = {}, out = [];
      current.variants.forEach(function (v) {
        var val = v.options[i];
        if (val != null && !seen[val]) { seen[val] = true; out.push(val); }
      });
      return out;
    }

    function matchVariant() {
      return current.variants.find(function (v) {
        return v.options.every(function (val, i) { return val === selected[i]; });
      }) || null;
    }

    // A value is selectable if some available variant carries it while honouring
    // the other already-picked options.
    function valueAvailable(optIndex, value) {
      return current.variants.some(function (v) {
        if (v.options[optIndex] !== value) return false;
        return v.available && v.options.every(function (val, i) {
          if (i === optIndex || selected[i] == null) return true;
          return val === selected[i];
        });
      });
    }

    function renderOptions() {
      optionsEl.innerHTML = '';
      current.optionNames.forEach(function (name, optIndex) {
        var group = document.createElement('div');
        group.className = 'art-choose__opt';

        var label = document.createElement('span');
        label.className = 'art-choose__opt-name';
        label.textContent = name;
        group.appendChild(label);

        var values = document.createElement('div');
        values.className = 'art-choose__values';
        valuesFor(optIndex).forEach(function (value) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'art-choose__value';
          b.textContent = value;
          if (selected[optIndex] === value) b.classList.add('is-selected');
          if (!valueAvailable(optIndex, value)) b.classList.add('is-unavailable');
          b.addEventListener('click', function () {
            selected[optIndex] = value;
            renderOptions();
            updateState();
          });
          values.appendChild(b);
        });
        group.appendChild(values);
        optionsEl.appendChild(group);
      });
    }

    function updateState() {
      var v = matchVariant();
      addBtn.classList.remove('is-added');
      if (v) {
        idInput.value = v.id;
        priceEl.textContent = v.price;
        if (v.image) setMedia(v.image);
        if (v.available) {
          addBtn.removeAttribute('disabled');
          addLabel.textContent = modal.getAttribute('data-t-add') || addLabel.textContent;
        } else {
          addBtn.setAttribute('disabled', '');
          addLabel.textContent = modal.getAttribute('data-t-sold-out') || addLabel.textContent;
        }
      } else {
        idInput.value = '';
        addBtn.setAttribute('disabled', '');
        addLabel.textContent = modal.getAttribute('data-t-unavailable') || addLabel.textContent;
      }
    }

    function open(id, trigger) {
      var data = getProductData(id);
      if (!data) return;
      current = data;
      lastFocus = trigger || null;

      var def = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];
      selected = def ? def.options.slice() : [];

      titleEl.textContent = data.title;
      var firstImg = (def && def.image) || data.image || '';
      setMedia(firstImg);
      imgEl.alt = data.title;
      viewLink.href = data.url;

      renderOptions();
      updateState();

      // showModal() opens it in the top layer: clear of .page-wrapper's
      // backdrop-filter (which would anchor a fixed overlay to the page), with
      // the background inert, focus trapped and Escape handled by the browser.
      if (!modal.open) modal.showModal();
      if (panel.focus) panel.focus();
    }

    function close() {
      if (modal.open) modal.close();
    }

    modal.addEventListener('close', function () {
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    });
    // A click on the dialog element itself is a click on the backdrop; the
    // panel is the only child that receives clicks.
    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });

    // Open is triggered from any product card on the page.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-choose]');
      if (trigger) {
        e.preventDefault();
        open(trigger.getAttribute('data-choose'), trigger);
      }
    });

    // Close handlers live on the modal itself (it's portaled out of the section).
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-choose-close]')) {
        e.preventDefault();
        close();
      }
    });

    // The shared add-to-cart handler dispatches this on success.
    document.addEventListener('art:cart:added', function (e) {
      if (e.detail && e.detail.form && e.detail.form.hasAttribute('data-choose-form')) {
        addBtn.classList.add('is-added');
        clearTimeout(closeTimer);
        closeTimer = setTimeout(close, 850);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initChoose);
  } else {
    initChoose();
  }
})();
